import { Box } from "@/components/atoms/Box";
import { LChildIcon } from "@/components/atoms/icons/LChildIcon";
import {
  Alert as BillingAlert,
  AlertCode,
  AlertLinkType,
  AlertType,
  CompanyQuery,
  CompanyStatus,
  OptionContractStatus,
  Plan,
  PlanContract,
} from "@/generated/graphql_billing";
import {
  ActiveService,
  byActiveThisMonth,
  useContractDetailPlan,
} from "@/libs/billing/plan";
import { FAQ_URL } from "@/libs/constants";
import paths from "@/libs/paths";
import { color } from "@/styles/theme";
import { css } from "@emotion/react";
import {
  Button,
  Font,
  Icon,
  ToolTip,
} from "@moneyforward/mfx_biz_react_components";
import { endOfDay, startOfDay } from "date-fns";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction } from "react";
import { Alert, Badge, ContractDetailPrice } from "../atoms";
import { NoContract } from "../molecules";
import { couponDiscountPeriodText } from "@/libs/billing/coupon";
import useFeatureFlags from "@/libs/hooks/useFeatureFlags";

type Props = {
  setIsCanecelPage: Dispatch<SetStateAction<boolean>>;
  company: CompanyQuery["company"];
  optionIds: string[];
  alerts: BillingAlert[];
  now: Date;
};

export const ContractDetail = (props: Props) => {
  const { setIsCanecelPage, optionIds, alerts, now } = props;
  const { featureFlags, loading, error } = useFeatureFlags();

  // TODO: Use the following code to handle loading and error states
  // However, if use this code, currently Runtime error occurs because of couponDiscountPeriodText function unstable behavior
  // See this comment to learn details
  // https://moneyforward.atlassian.net/browse/MFXID-2736?focusedCommentId=117283
  // if (loading) {
  //   return <p>Loading...</p>;
  // }
  // if (error) {
  //   return <p>Error: {error.message}</p>;
  // }

  const company = props.company;
  const router = useRouter();

  const planContract = company.planContract;
  const canceledPlanContracts =
    company?.canceledPlanContracts?.filter(byActiveThisMonth(now)) || [];

  const optionContracts = company.optionContracts
    ? company.optionContracts.filter(
        (optionContract) =>
          optionContract.status === OptionContractStatus.Contracted
      )
    : [];
  const filteredCanceledOptionContracts = company.canceledOptionContracts
    ? company.canceledOptionContracts
        .filter(
          (canceledOptionContract) =>
            startOfDay(new Date(canceledOptionContract.usingDateFrom)) <= now &&
            (!canceledOptionContract.usingDateTo ||
              now <= endOfDay(new Date(canceledOptionContract.usingDateTo)))
        )
        .flatMap((canceledOptionContract) =>
          JSON.stringify({
            id: canceledOptionContract.option.id,
            name: canceledOptionContract.option.name,
          })
        )
    : [];
  const canceledOptionContracts = Array.from(
    new Set(filteredCanceledOptionContracts)
  ).map((canceledOptionContract) => JSON.parse(canceledOptionContract)) as {
    id: string;
    name: string;
  }[];

  const coupons = planContract
    ? planContract.appliedCoupons ?? []
    : canceledPlanContracts
    ? canceledPlanContracts.flatMap(
        (canceledPlanContract) => canceledPlanContract.appliedCoupons ?? []
      )
    : [];

  const suspendedAlerts = alerts
    .filter((alert) => alert && alert.code === AlertCode.Suspended)
    .map((alert) => ({
      alertID: alert.alertID,
      alertType: alert.alertType,
      linkType: AlertLinkType.External,
      linkURL: FAQ_URL,
      message: alert.message,
    }));

  if (!planContract && canceledPlanContracts.length === 0) {
    return <NoContract company={company} />;
  }
  return (
    <div css={container}>
      {suspendedAlerts.length > 0 && (
        <Alert alerts={suspendedAlerts} linkText="詳しくはこちら" />
      )}
      {!planContract && (
        <Alert
          alerts={[
            {
              alertID: 1111,
              message:
                "今月末で解約が予定されています。来月から各サービスとオプションのご利用ができません。",
              alertType: AlertType.Warning,
              linkType: AlertLinkType.None,
              linkURL: "",
            },
          ]}
        />
      )}
      <div css={containerItem}>
        <Font variant="mb">プラン</Font>
        <ContractDetailPlan now={now} company={company} />
      </div>
      <div css={containerItem}>
        <Font variant="mb">オプション</Font>
        <div css={box}>
          {optionContracts.length > 0 || canceledOptionContracts.length > 0 ? (
            <div css={boxContentFlexStart}>
              <div css={boxContentLabel}>
                <Font css={{ color: color.gray600 }}>オプション名</Font>
              </div>
              <div css={boxContentColumn}>
                {canceledOptionContracts.map((canceledOptionContract) => (
                  <div key={canceledOptionContract.id}>
                    {!optionContracts.find(
                      (optionContract) =>
                        optionContract.option.id === canceledOptionContract.id
                    ) && (
                      <div css={boxContent}>
                        <Font>{canceledOptionContract.name}</Font>
                        {planContract && (
                          <Badge
                            fill={true}
                            variant="secondary"
                            text={"解約予定"}
                            toolTip={
                              <ToolTip
                                placement="bottom"
                                variant="guide"
                                text="今月末でオプションの解約が予定されています。解約を取り消すには、オプション追加から再度契約してください。"
                              >
                                <Icon name="help" size="sm" />
                              </ToolTip>
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {optionContracts.map((optionContract) => (
                  <div css={boxContent} key={optionContract.id}>
                    <Font>{optionContract.option.name}</Font>
                    <ContractDetailPrice
                      price={optionContract.option.priceIncludingTax}
                    />
                    {planContract && (
                      <Font
                        variant="s"
                        onClick={() =>
                          router.push(
                            `/option/option-cancel?id=${optionContract.id}`
                          )
                        }
                        css={linkButton}
                      >
                        解約
                      </Font>
                    )}
                  </div>
                ))}
                {planContract && optionIds.length > optionContracts.length && (
                  <div>
                    <Button
                      variant="secondary"
                      onClick={() => router.push("/option/add-option")}
                      disabled={company.status == CompanyStatus.Suspended}
                    >
                      追加
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div css={boxContentWithLink}>
                <Font>契約中のオプションはありません</Font>
              </div>
              {planContract && (
                <Button
                  variant="secondary"
                  onClick={() => router.push("/option/add-option")}
                  disabled={company.status == CompanyStatus.Suspended}
                >
                  追加
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      {coupons.length > 0 && (
        <div css={containerItem}>
          <Font variant="mb">クーポン</Font>
          <div css={box}>
            {coupons.map((coupon) => (
              <div key={coupon.id}>
                <div css={boxContent}>
                  <div css={boxContentLabel}>
                    <Font css={{ color: color.gray600 }}>クーポン名</Font>
                  </div>
                  <Font>{coupon.coupon.name}</Font>
                </div>
                <div css={boxContent}>
                  <div css={boxContentLabel}>
                    <Font css={{ color: color.gray600 }}>クーポン終了年月</Font>
                  </div>
                  <Font>
                    {couponDiscountPeriodText({
                      coupon: coupon.coupon,
                      from: coupon.applicatedOnFrom,
                    })}
                  </Font>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {planContract ? (
        company.status !== CompanyStatus.Suspended && (
          <div css={bottomContainer}>
            <div css={{ textAlign: "right" }}>
              <Font
                variant="s"
                onClick={() => {
                  if (featureFlags.CollectCancelReason.Enabled) {
                    router.push(paths.PlanCancelPreProcess);
                  } else {
                    setIsCanecelPage(true);
                  }
                }}
                css={linkButton}
              >
                すべて解約する
              </Font>
            </div>
          </div>
        )
      ) : (
        <div css={bottomContainer}>
          <div css={{ textAlign: "center" }}>
            <Button
              variant="secondary"
              onClick={() => {
                setIsCanecelPage(true);
              }}
            >
              解約の取消し
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ContractDetailPlan = ({
  now,
  company,
}: {
  now: Date;
  company: CompanyQuery["company"];
}) => {
  const router = useRouter();

  const {
    curPlanContract,
    expPlanContracts,
    curServices,
    diffServices,
    diffFeatures,
  } = useContractDetailPlan(now, company);

  return (
    <Box css={{ padding: "16px" }}>
      <div
        css={{
          display: "grid",
          gap: "16px",
          gridTemplateColumns: "154px minmax(0, 1fr)",
        }}
      >
        <div css={{ display: "flex", alignItems: "start", paddingTop: "6px" }}>
          <Font css={{ color: color.gray600 }}>プラン名</Font>
        </div>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            alignItems: "baseline",
            gap: "4px",
          }}
        >
          {/* current PlanContract */}
          {curPlanContract && (
            <CurrentPlan
              plan={curPlanContract.plan}
              onChangePlanButtonClick={() => router.push("/plan/change-plan")}
              disableChangePlanButton={
                company.status == CompanyStatus.Suspended
              }
            />
          )}
          {/* PlanContracts that are expiring at the end of the month */}
          {expPlanContracts && (
            <div css={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {expPlanContracts.map((pc: PlanContract) => (
                <CanceledPlan key={`pc-${pc.id}`} plan={pc.plan} />
              ))}
            </div>
          )}
        </div>

        {/* services */}
        <div css={{ display: "flex", alignItems: "start" }}>
          <Font css={{ color: color.gray600 }}>利用サービス</Font>
        </div>
        <div css={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {/* current PlanContract's services */}
          {curServices.map((svc) => (
            <ActiveServiceFeature key={`svc-${svc.id}`} service={svc} />
          ))}
          {/* services from expiring PlanContracts that are not in current PlanContract */}
          {diffServices.map((svc) => (
            <ActiveServiceFeature
              key={`svc-${svc.id}`}
              service={svc}
              showActiveUntilEndOfMonthBadge={!!curPlanContract}
            />
          ))}
        </div>

        {/* features unavailable next month message */}
        {curPlanContract && diffFeatures.length > 0 && (
          <FeatureUnavailableNextMonthMessage css={{ gridColumnStart: "2" }} />
        )}
      </div>
    </Box>
  );
};

const CurrentPlan = ({
  plan,
  hideChangePlanButton,
  disableChangePlanButton,
  onChangePlanButtonClick,
}: {
  plan: Plan;
  hideChangePlanButton?: boolean;
  disableChangePlanButton?: boolean;
  onChangePlanButtonClick?: () => void;
}) => {
  return (
    <div css={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <Font css={{ flexShrink: 1 }}>{plan.name}</Font>
      <ContractDetailPrice price={plan.priceIncludingTax} />
      {!hideChangePlanButton && (
        <Button
          variant="secondary"
          onClick={onChangePlanButtonClick}
          disabled={disableChangePlanButton}
        >
          プラン変更
        </Button>
      )}
    </div>
  );
};

const CanceledPlan = ({ plan }: { plan: Plan }) => {
  const pc = plan;
  return (
    <div
      key={pc.id}
      css={{ display: "flex", alignItems: "center", gap: "4px" }}
    >
      <Font>{plan.name}</Font>
      <Badge fill={true} variant="secondary" text="解約済" />
    </div>
  );
};

const ActiveServiceFeature = ({
  service,
  showActiveUntilEndOfMonthBadge = false,
}: {
  service: ActiveService;
  showActiveUntilEndOfMonthBadge?: boolean;
}) => {
  const svc = service;

  const featureRowCSS = (idx: number) => {
    if (idx === 0) {
      return css({});
    }
    return css({
      gridColumnStart: "2",
    });
  };
  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <div css={{ display: "flex", gap: "8px" }}>
        <Font>{svc.name}</Font>
        {showActiveUntilEndOfMonthBadge && <ActiveUntilEndOfMonthBadge />}
      </div>
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "20px minmax(0, 1fr)",
          gap: "4px 0px",
        }}
      >
        <div>
          {svc.features.length > 0 && (
            <LChildIcon
              height={13}
              width={20}
              css={{ position: "relative", top: "-3px", left: "2px" }}
            />
          )}
        </div>
        {svc.features.map((feature, idx) => (
          <div css={featureRowCSS(idx)} key={feature.id}>
            {feature.name}
          </div>
        ))}
      </div>
    </div>
  );
};

const ActiveUntilEndOfMonthBadge = () => {
  return (
    <Badge
      fill={true}
      variant="secondary"
      text={"利用終了予定"}
      toolTip={
        <ToolTip
          placement="bottom"
          variant="guide"
          text="今月末でサービスの利用終了が予定されています。利用終了予定を取り消すには、対象のサービスが含まれるプランに変更してください。"
        >
          <Icon name="help" size="sm" />
        </ToolTip>
      }
    />
  );
};

const FeatureUnavailableNextMonthMessage = (props) => {
  return (
    <Font {...props}>
      ※プランに付帯する機能はプラン変更により月初から利用できない可能性がございます。
    </Font>
  );
};

const container = css({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  marginTop: "16px",
});

const containerItem = css({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const box = css({
  border: `1px solid ${color.gray300}`,
  borderRadius: "4px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const boxContent = css({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const boxContentItem = css(boxContent, {
  alignSelf: "flex-end",
});

const boxContentAlignRight = css(boxContent, {
  alignSelf: "right",
});

const boxContentColumn = css({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const boxContentFlexStart = css(boxContent, {
  alignItems: "flex-start",
});

const boxContentLabel = css({
  width: "162px",
});

const linkButton = css({
  color: color.link,
  whiteSpace: "nowrap",
  "&:hover": {
    cursor: "pointer",
    textDecoration: "underline",
  },
});

const boxContentWithLink = css({
  marginBottom: "4px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const bottomContainer = css({
  borderTop: `1px solid ${color.gray300}`,
  paddingTop: "16px",
  marginTop: "16px",
});
